import { BoardChangeEvent } from "./types";

// Pure routing predicates that decide which on-demand view must react to a board change event.
// Keeping them here (not inside the hooks/components) makes the scoping rules unit-testable in
// isolation and documents, in one place, exactly what each kind of mutation invalidates.

// Whether a column (identified by the SET of statuses it aggregates) must re-fetch in response to
// a change event. `containsIssue` is whether the column currently holds the event's issue. Columns
// track only issue lifecycle/placement — comment/link/attachment/subtask/config changes never
// touch them, so those kinds are ignored and the column stays put.
export function columnShouldReload(event: BoardChangeEvent, statusIds: string[], containsIssue: boolean): boolean {
  switch (event.kind) {
    case "config.changed":
    case "comment.changed":
    case "links.changed":
    case "attachments.changed":
    case "issue.subtaskChanged":
      return false;
    case "issue.created":
      // Known landing status in this column → only this column; unknown (e.g. subtask promotion) → reload all.
      return event.statusId === undefined || statusIds.includes(event.statusId);
    case "issue.moved":
      // Source + destination only. `containsIssue` covers non-optimistic moves where the card
      // is still in its origin column when the signal fires.
      return (
        (event.toStatusId !== undefined && statusIds.includes(event.toStatusId)) ||
        (event.fromStatusId !== undefined && statusIds.includes(event.fromStatusId)) ||
        containsIssue
      );
    case "issue.updated":
      // The card I hold (or that just landed in me) may have changed; others are unaffected.
      return (event.statusId !== undefined && statusIds.includes(event.statusId)) || containsIssue;
    case "issue.deleted":
    case "issue.archived":
      // Only the column that still holds the card needs to drop it.
      return containsIssue;
    case "issue.restored":
      // The card re-enters an unknown column, so every column reloads to surface it.
      return true;
  }
}

// Whether the open issue-detail bundle (for `issueId`) must re-fetch in response to an event.
// The detail shows the issue's own fields plus its comments/links/attachments/subtasks, so it
// reacts to changes targeting its issue and to detail-sub-resource changes whose issue id is
// absent (edit/delete paths that only carry a comment/link/attachment id).
export function detailShouldReload(event: BoardChangeEvent, issueId: string): boolean {
  switch (event.kind) {
    case "config.changed":
    case "issue.created":
    case "issue.deleted":
      // A create never matches an already-open detail; a delete of this issue closes the panel.
      return false;
    case "issue.updated":
      // Optimistic edits are reconciled by the authoritative event that follows — don't
      // refetch the bundle on them (the server hasn't applied the change yet).
      if (event.optimistic) return false;
      return event.issueId === issueId;
    case "issue.moved":
    case "issue.archived":
    case "issue.restored":
      return event.issueId === issueId;
    case "comment.changed":
    case "links.changed":
    case "attachments.changed":
    case "issue.subtaskChanged":
      // Edit/delete paths may not know the issue id; reload when it's absent or matches.
      return event.issueId === undefined || event.issueId === issueId;
  }
}

// Whether an issue's own header fields (summary/status/priority/etc.) changed — i.e. the
// standalone Issue object behind the open panel needs re-resolving. Narrower than
// detailShouldReload: comment/link/attachment/subtask changes leave the header untouched.
export function issueHeaderChanged(event: BoardChangeEvent, issueId: string): boolean {
  switch (event.kind) {
    case "issue.updated":
      // Optimistic edits don't re-resolve the header; the authoritative event does.
      if (event.optimistic) return false;
      return event.issueId === issueId;
    case "issue.moved":
    case "issue.archived":
    case "issue.restored":
      return event.issueId === issueId;
    default:
      return false;
  }
}

// Whether the archived-issues list changed: an issue was archived (added), restored (removed),
// or permanently deleted (removed). Nothing else affects the archived set.
export function archivedListChanged(event: BoardChangeEvent): boolean {
  return event.kind === "issue.archived" || event.kind === "issue.restored" || event.kind === "issue.deleted";
}

// Whether the roadmap (issues with dates + their links) changed: any issue lifecycle/field
// change or a link change. Comments, attachments, subtasks and config never alter the roadmap.
export function roadmapChanged(event: BoardChangeEvent): boolean {
  switch (event.kind) {
    case "issue.updated":
      // The optimistic store patch already moved the roadmap; skip the stale refetch and
      // let the authoritative event reconcile.
      return !event.optimistic;
    case "issue.created":
    case "issue.moved":
    case "issue.deleted":
    case "issue.archived":
    case "issue.restored":
    case "links.changed":
      return true;
    default:
      return false;
  }
}

// Whether the reports (throughput / distribution / priority breakdown) need recomputing: any
// change to the issue set. Comments, links, attachments, subtasks and config don't move the
// numbers, so they don't trigger a recompute.
export function reportsChanged(event: BoardChangeEvent): boolean {
  switch (event.kind) {
    case "issue.updated":
      // Optimistic edits don't move the reported numbers until confirmed; the authoritative
      // event triggers the recompute.
      return !event.optimistic;
    case "issue.created":
    case "issue.moved":
    case "issue.deleted":
    case "issue.archived":
    case "issue.restored":
      return true;
    default:
      return false;
  }
}
